import React from 'react';
import Button from './Button';
import { CloseFriendMark, MirrorRoom, useCloseFriend } from './closefriends';

/**
 * FriendActionButton — renders the right control for a person in a search result
 * based on the viewer's relationship to them. Already-friends shows a label (no
 * add button); a pending request you sent shows a disabled "REQUESTED"; a request
 * they sent you links to your friends page; otherwise an "+ ADD" button.
 *
 * @param {string} status - 'friends' | 'request_sent' | 'request_received' | 'none'
 * @param {boolean} [requestSent] - optimistic flag after clicking add this session
 * @param {boolean} [sending] - request in flight
 * @param {() => void} onAdd - send a friend request
 * @param {() => void} [onRespond] - go respond to a received request
 * @param {string} [userId] - with it, a friend also gets the close-friends mark
 * @param {boolean} [isClose] - the viewer has marked them close
 * @param {string} [name] - for the room's sub-line and the mark's label
 *
 * The mark's mirror room covers the whole row, so the row needs `relative`, and
 * inside a padded panel the panel needs `overflow-hidden` plus `--cf-bleed` set
 * to its padding (defaults to 24px, i.e. p-6).
 */
export default function FriendActionButton({ status, requestSent, sending, onAdd, onRespond, userId, isClose, name }) {
  const effective = requestSent ? 'request_sent' : (status || 'none');

  if (effective === 'friends') {
    const label = (
      <span className="font-mono text-xs font-bold uppercase tracking-wider text-gray-400 px-3 py-1.5 whitespace-nowrap">
        ✓ FRIENDS
      </span>
    );
    return userId ? <FriendsWithMark userId={userId} isClose={isClose} name={name} label={label} /> : label;
  }

  if (effective === 'request_sent') {
    return (
      <Button size="sm" variant="secondary" disabled>
        REQUESTED
      </Button>
    );
  }

  if (effective === 'request_received') {
    return (
      <Button size="sm" variant="secondary" onClick={onRespond}>
        RESPOND →
      </Button>
    );
  }

  return (
    <Button size="sm" variant="primary" onClick={onAdd} disabled={sending}>
      {sending ? 'SENDING…' : '+ ADD'}
    </Button>
  );
}

function FriendsWithMark({ userId, isClose, name, label }) {
  const close = useCloseFriend(userId, isClose, name);
  return (
    <>
      {close.error ? (
        <span role="alert" className="font-mono text-xs font-bold uppercase tracking-wider text-black px-3 py-1.5 whitespace-nowrap">
          {close.error}
        </span>
      ) : label}
      <CloseFriendMark size={32} on={close.on} onToggle={close.toggle} name={name} />
      <MirrorRoom q={close.q} surface="dashboard" first={close.first} />
    </>
  );
}
